/**
 * Where a freshly generated site goes.
 *
 * ⚠️ ONE PLACE, BECAUSE THERE ARE THREE CALL SITES AND ONLY ONE RULE. Generation is reached from the
 * landing page, from the sites list's new-site form, and from the landing page again after a sign-in
 * round trip — the same three that made `track()` live inside `api.generate` rather than on the
 * buttons (CLAUDE.md §4.1). The rule they share is a single branch, and a branch copied three times
 * is a branch that will be got wrong in one of them.
 *
 * ⚠️ THE BRANCH IS ON THE ID, NEVER ON WHETHER THE PERSON LOOKS SIGNED IN. The server decides whether
 * it stored anything — a stranger gets a document and no record (see `routes/ai.ts`) — and `id` is how
 * it says so. Asking the browser's own idea of the session instead would be reading a second copy of
 * a fact the reply already carries, and the two can disagree: a session can expire between the click
 * and the answer, which is precisely the moment this must not send somebody to `/s/null`.
 */

import type { Site } from '@ksa/model'
import { newDraftId, writeDemo } from './demo.ts'

export type Generated = { id: string | null; document: Site }

/**
 * The path to open, having put the document somewhere it can be found.
 *
 * ⚠️ IT WRITES BEFORE IT RETURNS, so the editor at the other end of the navigation always finds
 * something. Returning a path and leaving the caller to store the document is two steps that must
 * happen in order, in two files, with a navigation in between — which is the shape of bug that shows
 * up as an empty editor for one person and never reproduces.
 */
export function destinationFor(created: Generated): string {
  if (created.id) return `/s/${created.id}`

  const draft = newDraftId()
  writeDemo(draft, 'draft', { document: created.document, changed: false })
  return `/draft/${draft}`
}
